import { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';
import ScrollToTop from '@/components/base/ScrollToTop';
import VideoCard from '@/components/feature/VideoCard';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, videos, articles } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'dianshi');
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'rating'>('hot');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];
  const isZixun = activeCategory === 'zixun';

  const filteredVideos = useMemo(() => {
    if (isZixun) return [];
    const filtered = videos.filter((v) => v.category === activeCategory);
    if (sortBy === 'hot') return [...filtered].sort((a, b) => parseFloat(b.views.replace('万', '')) - parseFloat(a.views.replace('万', '')));
    if (sortBy === 'new') return [...filtered].sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
    return [...filtered].sort((a, b) => b.rating - a.rating);
  }, [activeCategory, sortBy, isZixun]);

  const filteredArticles = useMemo(() => {
    if (!isZixun) return [];
    return articles;
  }, [isZixun]);

  const categoryMetaTitle = isZixun ? '影视资讯' : currentCategory.name;
  const categoryMetaDesc = isZixun ? '黄瓜tv影视资讯频道，提供最新影视动态、行业分析和深度评论。' : `黄瓜tv${currentCategory.name}频道，${currentCategory.description}。海量高清${currentCategory.name}资源在线观看。`;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `黄瓜tv的${currentCategory.name}内容多久更新一次？`,
        acceptedAnswer: { '@type': 'Answer', 'text': `黄瓜tv每日更新${currentCategory.name}内容，确保用户能看到最新最热的资源。` },
      },
      {
        '@type': 'Question',
        name: `黄瓜tv${currentCategory.name}支持高清播放吗？`,
        acceptedAnswer: { '@type': 'Answer', 'text': `黄瓜tv所有${currentCategory.name}内容均支持高清甚至4K画质播放，提供最佳观影体验。` },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={categoryMetaTitle}
        description={categoryMetaDesc}
        keywords={`黄瓜tv,黄瓜tv在线观看,黄瓜tv${currentCategory.name},${currentCategory.name}在线观看`}
        canonicalUrl={`https://huanggua.tv/category/${activeCategory}`}
        schema={faqSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <Breadcrumb items={[
            { label: '首页', path: '/' },
            { label: currentCategory.name },
          ]} />

          <div className="w-full px-4 md:px-6 py-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-8">
              {currentCategory.name}
              <span className="text-base font-normal text-foreground-500 ml-2">- {currentCategory.description}</span>
            </h1>

            {/* Category Switcher */}
            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
              <Link
                to="/category/zixun"
                onClick={() => setActiveCategory('zixun')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeCategory === 'zixun'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                }`}
              >
                <i className="ri-article-line text-sm"></i>
                资讯
              </Link>
            </div>

            {/* Sort */}
            {!isZixun && (
              <div className="flex items-center gap-2 mb-6">
                <span className="text-sm text-foreground-500">排序：</span>
                {[
                  { value: 'hot' as const, label: '最热' },
                  { value: 'new' as const, label: '最新' },
                  { value: 'rating' as const, label: '评分最高' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {/* Content Grid */}
            {!isZixun ? (
              filteredVideos.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                  {filteredVideos.map((video) => (
                    <VideoCard key={video.id} {...video} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                    <i className="ri-inbox-line text-3xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
                </div>
              )
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 md:gap-6">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.id} {...article} />
                ))}
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}